// App shell — header (Smokeball wordmark + pending-approvals badge +
// nav links + sign-out), main slot, footer.
//
// **Why a manual layout, not a CSS framework:** the SPA at v1 is
// shell-only. Adding Tailwind / shadcn now would lock the UX
// direction before fn-1.29 fills in the domain views. We use plain
// CSS classes; fn-1.29 (or a dedicated design pass) can swap to
// a system later without changing the routing or page wrapping.
//
// **Navigation routes** are stubbed against the routes registered in
// `app.tsx`. The route components themselves are empty placeholders
// (fn-1.29 fills them in) — but the nav exists from day one so the
// header structure is testable.

import { NavLink, Outlet } from 'react-router-dom';

import { PendingApprovalsBadge } from './pending-approvals-badge.js';

export interface AppLayoutProps {
  /**
   * Optional banner element rendered above the main slot. Used by
   * the error-boundary and the "session refreshing..." status
   * notice. Most renders pass nothing.
   */
  readonly banner?: React.ReactNode;
}

/**
 * The top-level app shell. Rendered by every authenticated route via
 * React Router's `Outlet`.
 */
export function AppLayout(props: AppLayoutProps): React.ReactElement {
  return (
    <div className="app-shell app-shell--sidebar">
      <aside className="app-sidebar" aria-label="Primary navigation">
        <a className="brand" href="/" aria-label="Smokeball AI home">
          <span className="brand-mark" aria-hidden="true">
            Smokeball
          </span>
          <span className="brand-suffix">AI</span>
        </a>
        <nav className="app-nav app-nav--vertical" aria-label="Primary">
          <NavLink to="/agents" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Agents
          </NavLink>
          <NavLink to="/skills" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Skills
          </NavLink>
          <NavLink to="/files" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Files
          </NavLink>
          <NavLink to="/runs" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Sessions
          </NavLink>
          <NavLink to="/approvals" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Approvals
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => (isActive ? 'is-active' : '')}>
            Settings
          </NavLink>
        </nav>
        <div className="app-sidebar-bottom">
          <PendingApprovalsBadge />
          <a className="signout-link" href="/logout">
            Sign out
          </a>
        </div>
      </aside>
      <div className="app-main-column">
        {props.banner !== undefined && (
          <output className="app-banner" aria-live="polite">
            {props.banner}
          </output>
        )}
        <main className="app-main">
          <Outlet />
        </main>
        <footer className="app-footer">
          <small>© Smokeball · AI Platform</small>
        </footer>
      </div>
    </div>
  );
}
